import { useCallback, useEffect, useState } from "react";

import Markdown from "markdown-to-jsx";
import Image from "next/image";
import { useRouter } from "next/router";

import SpheronLogo from "../../../assets-protocol-v4/spheron-logo.svg";
import Meta from "../../../components/Meta";
import Footer from "../../../components-protocol-v4/Footer";
import NavBar from "../../../components-protocol-v4/NavBar";
import PrimaryButton from "../../../components-protocol-v4/PrimaryButton";
import ArrowRight from "../../../protocol-assets/arrow-right.svg";
import { RedirectLinks } from "../../../utils/common";
import { posts } from "../../../utils/get-all-changelogs";
import { mainMeta } from "../../../utils/meta-data";
import Dot from "../../Dot";
import { NavbarItems } from "../../NavBar/Navbar";
import styles from "../../Styles/changelog.module.scss";

enum Location {
  NEXT = "next",
  PREVIOUS = "previous",
}
interface IAuthorWithPositions {
  author: string;
  position: string;
}
interface IIndividual {
  id: number;
  image: string;
  title: string;
  description: string;
  date: string;
  authorWithPositions: IAuthorWithPositions[];
  children?: string;
}
interface IPost {
  id: number;
  link: string;
  title: string;
  shortDescription: string;
  keywords: Array<string>;
  image: string;
  date: string;
  authorWithPositions: IAuthorWithPositions[];
  module: IIndividual;
  content: string;
}

interface IProps {
  children: string;
  meta: IPost;
}

const SingleChangelog = ({ meta, children }: IProps) => {
  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false);
  const newId = posts.findIndex((post: IPost) => post?.module?.id === meta?.id);

  const router = useRouter();
  // const thumbnailImagePath = `https://spheron.network/changelog/${meta.image}`;
  const handleLocation = (location: Location) => {
    if (newId >= 0) {
      let nextId;
      if (location === Location.NEXT) {
        nextId = newId + 1;
      } else if (location === Location.PREVIOUS) {
        nextId = newId - 1;
      }
      router.push(`/changelog/${posts[nextId]?.link}`);
    }
  };

  useEffect(() => {
    if (showCopiedTooltip) {
      setTimeout(() => {
        setShowCopiedTooltip(false);
      }, 1000);
    }
  }, [showCopiedTooltip]);

  useEffect(() => {
    if (window.scrollY > 15)
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 100);
  }, []);

  const [lastScrollY, setLastScrollY] = useState(0);
  const [hidden, setHidden] = useState(false);

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    if (currentScrollY > lastScrollY) {
      setHidden(true);
    } else {
      setHidden(false);
    }
    setLastScrollY(currentScrollY);
  }, [lastScrollY]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  return (
    <div className="bg-[#F0F0F3] p-5 w-full text-protocol-base">
      <Meta
        title={mainMeta.title}
        desc={mainMeta.desc}
        keyword={mainMeta.keyword}
        icon={mainMeta.icon}
        metaImage={mainMeta.metaImage}
        url={mainMeta.url}
      />
      <main className="bg-white rounded-md border-protocolNew-border">
        <NavBar
          page={NavbarItems.RESOURCES}
          buttonContent="Rent Now 🚀"
          buttonOnClick={() => window.open(RedirectLinks.CONSOLE_APP, "_blank")}
        />

        <div className="w-full border-b bg-white rounded-t-md">
          <div className="h-[68px] border-b bg-white py-2 justify-center items-center relative lg:hidden flex">
            <div className="absolute left-6 h-full border-x border-protocolNew-border w-[calc((100%_-_45px)_/_3)]"></div>
            <div className="absolute right-6 h-full border-x border-protocolNew-border w-[calc((100%_-_45px)_/_3)]"></div>
            <div
              onClick={() => router.push("/")}
              className="cursor-pointer w-[calc((100%_-_90px)_/_3)] h-full flex items-center"
            >
              <Image src={SpheronLogo} alt="spheron" className="" />
            </div>
          </div>
          <div className="h-[68px] w-full hidden lg:flex justify-between">
            <div className="relative w-sides shrink-0 grow-0 flex justify-center items-center">
              <div
                onClick={() => router.push("/")}
                className="cursor-pointer w-[100px] h-[34px]"
              >
                <Image src={SpheronLogo} alt="spheron" />
              </div>
            </div>
            <div className="w-content shrink-0 grow-0 border-x flex justify-center h-full">
              <div className="w-1/3 grow-0 shrink-0 border-x"></div>
            </div>
            <div className="w-sides grow-0 relative">
              <PrimaryButton
                variant="light"
                className="rounded-tr-md"
                onClick={() => {
                  window.open(RedirectLinks.CONSOLE_APP, "_blank");
                }}
              >
                Deploy Now
              </PrimaryButton>
            </div>
          </div>
        </div>

        <section className="relative font-sans mx-auto px-3 flex flex-col w-content-mobile lg:w-content border-x">
          <div className="w-full h-full absolute top-0 left-0 flex">
            <div className="w-1/3 relative h-full mx-auto border-x"></div>
          </div>
          <Dot className="-translate-x-[4.5px] -translate-y-[4.5px] top-0 -left-0 lg:-top-0 lg:-left-0" />
          <Dot className="translate-x-[4.5px] -translate-y-[4.5px] top-0 -right-0 lg:-top-0 lg:-right-0" />

          <div className="animate-landing-blur opacity-0 z-10 flex flex-col gap-2 text-center pb-24">
            <div className="text-2xl leading-9 lg:text-xxxl lg:leading-[44px] uppercase pt-[180px] font-sans">
              {meta?.title}
              {showCopiedTooltip && (
                <span
                  className="text-sm bg-protocol-card-bg rounded-lg h-auto cursor-default
                      absolute sm:mt-3 sm:ml-4 -mt-8 -ml-12 px-3 py-2"
                >
                  Copied
                </span>
              )}
            </div>
            <h3 className="text-xs leading-4 font-roboto text-protocolNew-light-sub-text">
              {meta?.date}
            </h3>
          </div>
        </section>

        <section className="relative font-sans mx-auto px-3 border-x w-content-mobile lg:w-content">
          <div className="w-full h-full absolute top-0 left-0 flex">
            <Dot className="absolute bottom-[-5px] left-[-3.5px] z-10" />
            <Dot className="absolute bottom-[-5px] right-[-3.5px] z-10" />
            <div className="w-1/3 relative h-full mx-auto border-x">
              <Dot className="hidden lg:block absolute bottom-[-5px] left-[-5px]" />
              <Dot className="hidden lg:block absolute bottom-[-5px] right-[-4.5px]" />
            </div>
          </div>
          <div
            data-aos="fade-up"
            data-aos-once="true"
            data-aos-duration="600"
            data-aos-delay="0"
            className={`isolate w-[95%] mb-16 ${styles.markdown_area}`}
          >
            <Markdown>{children}</Markdown>
          </div>
          <div
            data-aos="fade-up"
            data-aos-once="true"
            data-aos-duration="600"
            data-aos-delay="0"
            className="isolate pl-10 flex mb-16 flex-col md:flex-row md:flex-wrap gap-x-10 gap-4 lg:gap-y-5"
          >
            {meta?.authorWithPositions.map((item: any, index: number) => (
              <div key={index}>
                <h3 className="font-jetbrains text-protocolNew-base uppercase text-[14px] leading-[14px]">
                  {item.author}
                </h3>
                <p className="font-inter text-protocolNew-sub-text text-[12px] leading-[24px]">
                  {item.position}
                </p>
              </div>
            ))}
          </div>
          <div
            data-aos="fade-up"
            data-aos-once="true"
            data-aos-duration="600"
            data-aos-delay="0"
            className="isolate flex pb-16 lg:pb-40 md:flex-row flex-col md:items-center"
          >
            <span
              onClick={() => router.push(`/changelog`)}
              className="cursor-pointer flex grow items-center font-basic text-[14px] leading-[24px] md:-ml-[1rem]"
            >
              <Image
                src={ArrowRight}
                alt="arrow"
                height={25}
                width={25}
                className="transition rotate-180"
              />
              Back to Changelog
            </span>
            <span className="flex gap-4 mt-5 md:mt-0 w-96 md:justify-end pb-8">
              {newId > 0 && (
                <>
                  {newId !== 0 && (
                    <div className="w-28 h-11">
                      <PrimaryButton
                        className="w-28 h-11"
                        variant="dark"
                        onClick={() => handleLocation(Location.PREVIOUS)}
                      >
                        Previous
                      </PrimaryButton>
                    </div>
                  )}
                </>
              )}
              <div className="w-28 h-11">
                <PrimaryButton
                  variant="dark"
                  onClick={() => handleLocation(Location.NEXT)}
                >
                  Next
                </PrimaryButton>
              </div>
            </span>
          </div>
        </section>
        <Footer />
      </main>
    </div>
  );
};

export default SingleChangelog;
